// Mão de obra do mês (06/10/2026) — Financeiro › DRE & Resultado › 👷 Mão de obra.
//
// Substitui a "cobertura e alocação padrão" (Fase 2, 17/08): a regra mudou e
// ficou simples — a remuneração paga a cada pessoa no mês segue as horas que
// ela anotou; o que não foi anotado fica parado até ser corrigido. Esta tela
// mostra, por pessoa, quanto foi pago, as horas, e para onde o valor foi.
// Só leitura: os números saem do último cálculo do mês (o mesmo do resultado
// por contrato). A conta e os textos ficam em maoObraRegras.ts (testados).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, App as AntApp, Button, Card, DatePicker, Empty, Flex, Input, Modal, Segmented, Space, Spin, Statistic, Table, Tag, Tooltip, Typography,
} from 'antd';
import { useSearchParams } from 'react-router-dom';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import TotalHoras from '../operacional-horas/TotalHoras';
import {
  PessoaMaoObra, RespostaMaoObra, MOTIVO_PENDENTE, totaisDoMes, filtrarPessoas, minutosDe, reais, contaDaPessoa, mesAnterior,
  RespostaHorasPessoa, EntradaHora, ModoHoras, DESTINO_HORA, paraOndeFoi, entradasVisiveis, horasMudaram, competenciaDaUrl, diaCurto,
} from './maoObraRegras';

const { Text, Paragraph } = Typography;

async function api(path: string, opts?: RequestInit) {
  const token = await getToken();
  const r = await fetch(`${API_URL}${path}`, {
    ...opts, headers: { Authorization: token, 'Content-Type': 'application/json', ...(opts?.headers || {}) },
  });
  let d: any = await r.json().catch(() => ({}));
  if (d && typeof d === 'object' && 'body' in d && typeof d.body === 'string') { try { d = JSON.parse(d.body); } catch {} }
  if (!r.ok || d?.erro) throw new Error(d?.erro || `Erro ${r.status}`);
  return d;
}

export default function MaoObraMes() {
  const { message } = AntApp.useApp();
  // ?competencia=2026/09&pessoa=<id>: o aviso da Margem por setor abre o mês e a pessoa (15/10/2026)
  const [sp, setSp] = useSearchParams();
  const compUrl = competenciaDaUrl(sp.get('competencia'));
  const pessoaUrl = useRef<string | null>(sp.get('pessoa'));
  const [comp, setComp] = useState<Dayjs>(dayjs((compUrl || mesAnterior()).replace('/', '-') + '-01'));
  const [dados, setDados] = useState<RespostaMaoObra | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [falhou, setFalhou] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const [aberta, setAberta] = useState<PessoaMaoObra | null>(null);
  const [recalculando, setRecalculando] = useState(false);
  // as horas da pessoa aberta, entrada por entrada (15/10/2026: "como o usuário preencheu")
  const [horasP, setHorasP] = useState<RespostaHorasPessoa | null>(null);
  const [carregandoHoras, setCarregandoHoras] = useState(false);
  const [falhaHoras, setFalhaHoras] = useState<string | null>(null);
  const [modoHoras, setModoHoras] = useState<ModoHoras>('sem');
  const [tentativaHoras, setTentativaHoras] = useState(0);
  const competencia = comp.format('YYYY/MM');
  const pedido = useRef(0);   // trocar de mês rápido: vale só a resposta do último pedido

  const carregar = useCallback(async () => {
    const meu = ++pedido.current;
    setCarregando(true);
    try {
      const d: RespostaMaoObra = await api(`/financeiro/fechamento-cc/mao-obra?competencia=${encodeURIComponent(competencia)}`);
      if (meu !== pedido.current) return;
      if (!d || !Array.isArray(d.pessoas)) throw new Error('resposta inesperada do servidor');
      setDados(d); setFalhou(null);
    } catch (e: any) {
      // falha ao carregar não vira "ninguém recebeu"
      if (meu === pedido.current) { setDados(null); setFalhou(e.message || 'erro'); }
    } finally { if (meu === pedido.current) setCarregando(false); }
  }, [competencia]);
  useEffect(() => { carregar(); }, [carregar]);

  // veio do aviso da Margem com a pessoa: abre a janela dela UMA vez, no mês do atalho, e tira o
  // atalho do endereço (senão a janela reabre ao voltar para a aba ou apertar F5)
  useEffect(() => {
    if (!dados || !pessoaUrl.current) return;
    if (compUrl && dados.competencia !== compUrl) return;   // resposta de outro mês: espera a certa
    const p = dados.pessoas.find((x) => x.profissional_id === pessoaUrl.current);
    pessoaUrl.current = null;
    if (p) setAberta(p);
    const next = new URLSearchParams(sp);
    next.delete('pessoa'); next.delete('competencia');
    setSp(next, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dados]);

  useEffect(() => {
    setHorasP(null); setFalhaHoras(null);
    if (!aberta || !aberta.profissional_id) return;
    let vivo = true;
    setCarregandoHoras(true);
    setModoHoras(aberta.h_sem_destino > 0 ? 'sem' : 'todas');
    api(`/financeiro/fechamento-cc/mao-obra/horas?competencia=${encodeURIComponent(competencia)}&profissional_id=${encodeURIComponent(aberta.profissional_id)}`)
      .then((d: RespostaHorasPessoa) => { if (vivo) setHorasP(d && Array.isArray(d.entradas) ? d : null); })
      // falha ao carregar não vira "a pessoa não anotou nada"
      .catch((e: any) => { if (vivo) setFalhaHoras(e.message || 'erro'); })
      .finally(() => { if (vivo) setCarregandoHoras(false); });
    return () => { vivo = false; };
  }, [aberta, competencia, tentativaHoras]);

  async function recalcular() {
    setRecalculando(true);
    try {
      await api('/financeiro/fechamento-cc/recalcular', { method: 'POST', body: JSON.stringify({ competencia }) });
      message.success(`${comp.format('MM/YYYY')} recalculado.`);
      await carregar();
    } catch (e: any) { message.error(e.message); }
    finally { setRecalculando(false); }
  }

  const pessoas = useMemo(() => filtrarPessoas(dados?.pessoas || [], busca), [dados, busca]);
  const tot = useMemo(() => totaisDoMes(dados?.pessoas || []), [dados]);

  const colunas = [
    { title: 'Pessoa', key: 'nome', render: (_: any, p: PessoaMaoObra) => (
      <Text strong={!!p.profissional_id} type={p.profissional_id ? undefined : 'danger'}>{p.nome}</Text>) },
    { title: 'Pago no mês', key: 'pago', align: 'right' as const, width: 130, render: (_: any, p: PessoaMaoObra) => reais(p.pago_mes) },
    { title: 'Horas anotadas', key: 'horas', align: 'right' as const, width: 120,
      render: (_: any, p: PessoaMaoObra) => <TotalHoras minutos={minutosDe(p.h_total)} vazio="—" alinhar="end" tamanhoDecimal={11} /> },
    { title: 'R$/hora', key: 'rs', align: 'right' as const, width: 110,
      render: (_: any, p: PessoaMaoObra) => (p.rs_hora > 0 && p.motivo_pendente !== 'mes_em_andamento' ? reais(p.rs_hora) : <Text type="secondary">—</Text>) },
    { title: 'Foi para contratos', key: 'ctr', align: 'right' as const, width: 150, render: (_: any, p: PessoaMaoObra) => (
      p.valor_contratos > 0 ? <Space direction="vertical" size={0} style={{ alignItems: 'flex-end' }}>
        <span>{reais(p.valor_contratos)}</span>
        <Text type="secondary" style={{ fontSize: 11 }}>{(p.contratos || []).length} contrato(s)</Text></Space>
        : <Text type="secondary">—</Text>) },
    { title: <Tooltip title="Horas em Administrativo, Comercial e outros centros internos: é despesa, repartida entre os contratos pela receita.">Despesa (centros internos)</Tooltip>,
      key: 'int', align: 'right' as const, width: 150,
      render: (_: any, p: PessoaMaoObra) => (p.valor_interno > 0 ? reais(p.valor_interno) : <Text type="secondary">—</Text>) },
    { title: 'Parado', key: 'pend', align: 'right' as const, width: 190, render: (_: any, p: PessoaMaoObra) => {
      if (!(p.valor_pendente > 0)) return <Text type="secondary">—</Text>;
      const m = p.motivo_pendente ? MOTIVO_PENDENTE[p.motivo_pendente] : null;
      return <Space direction="vertical" size={0} style={{ alignItems: 'flex-end' }}>
        <span>{reais(p.valor_pendente)}</span>
        {m && <Tooltip title={m.ajuda}><Tag color={m.cor} style={{ marginInlineEnd: 0 }}>{m.rotulo}</Tag></Tooltip>}
      </Space>;
    } },
  ];

  return (
    <div>
      <Paragraph type="secondary" style={{ fontSize: 13, maxWidth: 980 }}>
        A remuneração paga a cada pessoa no mês é dividida pelas horas que ela anotou. Hora em contrato vira custo do
        contrato; hora em Administrativo, Comercial e outros centros internos vira despesa, repartida entre os contratos
        pela receita; o que não foi anotado fica <b>parado</b> até ser corrigido. Quem tem divisão fixa na ficha não aparece aqui.
      </Paragraph>
      <Flex wrap="wrap" gap={8} align="center" style={{ marginBottom: 12 }}>
        <DatePicker picker="month" value={comp} format="MM/YYYY" allowClear={false} onChange={(v) => v && setComp(v)} />
        <Input.Search allowClear placeholder="Buscar pessoa…" style={{ width: 220 }} onChange={(e) => setBusca(e.target.value)} />
        <Tooltip title="Refaz o resultado por contrato deste mês com os lançamentos e as horas de agora.">
          <Button icon={<ReloadOutlined />} loading={recalculando} onClick={recalcular} disabled={!!dados?.congelado}>Recalcular o mês</Button>
        </Tooltip>
        {dados?.execucao && <Text type="secondary" style={{ fontSize: 12 }}>calculado em {dayjs(dados.execucao.calculado_em).format('DD/MM/YYYY HH:mm')}</Text>}
      </Flex>

      {carregando ? <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>
        : falhou ? <Alert type="error" showIcon message="Não foi possível carregar a mão de obra do mês." description={falhou}
            action={<Button size="small" onClick={carregar}>Tentar de novo</Button>} />
        : !dados ? null
        : dados.congelado ? <Alert type="info" showIcon message={`A análise por contrato começa em ${dados.corte.split('/').reverse().join('/')}.`}
            description="Os meses anteriores ficam como estão." />
        : !dados.execucao ? <Alert type="warning" showIcon message="Este mês ainda não foi calculado." description='Use "Recalcular o mês".' />
        : dados.regra_antiga ? <Alert type="warning" showIcon message="Este mês foi calculado antes da regra das horas anotadas (06/10/2026)."
            description='Use "Recalcular o mês" para ver a divisão pelas horas.' />
        : (<>
          {dados.em_andamento && <Alert type="info" showIcon style={{ marginBottom: 12 }}
            message="Mês em andamento: a divisão pelas horas só acontece quando o mês fecha. Até lá, todo o valor fica parado." />}
          <Flex wrap="wrap" gap={12} style={{ marginBottom: 12 }}>
            <Card size="small" style={{ minWidth: 190 }}><Statistic title="Pago no mês" value={reais(tot.pago)} /></Card>
            <Card size="small" style={{ minWidth: 190 }}><Statistic title="Foi para contratos" value={reais(tot.contratos)} /></Card>
            <Card size="small" style={{ minWidth: 190 }}><Statistic title="Despesa (centros internos)" value={reais(tot.interno)} /></Card>
            <Card size="small" style={{ minWidth: 190 }}>
              <Statistic title="Parado (não anotado ou sem contrato)" value={reais(tot.pendente)}
                valueStyle={tot.pendente > 0 ? { color: '#d46b08' } : undefined} />
            </Card>
          </Flex>
          {pessoas.length === 0
            ? <Empty description={busca ? 'Ninguém com esse nome no mês.' : 'Nenhuma remuneração nem hora anotada neste mês.'} />
            : <Table rowKey={(p) => p.profissional_id || 'sem-pessoa'} size="small" dataSource={pessoas} columns={colunas}
                pagination={false} scroll={{ x: 1000 }}
                onRow={(p) => ({ onClick: () => setAberta(p), style: { cursor: 'pointer' } })} />}
        </>)}

      <Modal open={!!aberta} onCancel={() => setAberta(null)} footer={null} width="94vw"
        style={{ maxWidth: 1240, top: 16 }} styles={{ body: { maxHeight: 'calc(100vh - 130px)', overflowY: 'auto' } }}
        title={aberta ? `${aberta.nome} — ${comp.format('MM/YYYY')}` : ''}>
        {aberta && (<>
          <Paragraph style={{ marginBottom: 12 }}>{contaDaPessoa(aberta)}</Paragraph>
          {aberta.profissional_id && aberta.pago_mes > 0 && aberta.h_total > 0 && aberta.motivo_pendente !== 'mes_em_andamento' && (
            <Flex wrap="wrap" gap={16} style={{ marginBottom: 12 }}>
              <span><Text type="secondary">Em contratos:</Text> <TotalHoras minutos={minutosDe(aberta.h_contrato)} vazio="0 h" alinhar="start" /></span>
              <span><Text type="secondary">Centros internos:</Text> <TotalHoras minutos={minutosDe(aberta.h_interno)} vazio="0 h" alinhar="start" /></span>
              <span><Text type="secondary">Sem contrato:</Text> <TotalHoras minutos={minutosDe(aberta.h_sem_destino)} vazio="0 h" alinhar="start" /></span>
            </Flex>
          )}
          {aberta.motivo_pendente && MOTIVO_PENDENTE[aberta.motivo_pendente] && aberta.valor_pendente > 0 && (
            <Alert type="warning" showIcon style={{ marginBottom: 12 }}
              // o valor só aparece aqui quando a frase de cima não o disse (parte das horas sem contrato)
              message={aberta.motivo_pendente === 'horas_sem_destino'
                ? `${reais(aberta.valor_pendente)} parados — ${MOTIVO_PENDENTE[aberta.motivo_pendente].rotulo}`
                : `Parado — ${MOTIVO_PENDENTE[aberta.motivo_pendente].rotulo}`}
              description={MOTIVO_PENDENTE[aberta.motivo_pendente].ajuda} />
          )}
          {(aberta.contratos || []).length > 0 && (
            <Table rowKey="contrato_id" size="small" pagination={false} dataSource={aberta.contratos}
              columns={[
                { title: 'Contrato', key: 'c', render: (_: any, c: any) => <span><b>{c.numero}</b>{c.titulo ? ` — ${c.titulo}` : ''}</span> },
                { title: 'Horas', key: 'h', align: 'right' as const, width: 110, render: (_: any, c: any) => <TotalHoras minutos={minutosDe(c.horas)} alinhar="end" tamanhoDecimal={11} /> },
                { title: 'Valor', key: 'v', align: 'right' as const, width: 130, render: (_: any, c: any) => reais(c.valor) },
                { title: 'De onde vieram as horas', key: 'f', width: 190, render: (_: any, c: any) => (c.fontes || []).join(' + ') },
              ]} />
          )}
          {aberta.profissional_id && (
            <div style={{ marginTop: 16 }}>
              <Flex justify="space-between" align="center" wrap="wrap" gap={8} style={{ marginBottom: 8 }}>
                <Text strong>Como a pessoa anotou as horas</Text>
                {horasP && (
                  <Segmented value={modoHoras} onChange={(v) => setModoHoras(v as ModoHoras)} options={[
                    { label: `Sem contrato (${horasP.entradas.filter((e) => e.destino === 'sem_destino').length})`, value: 'sem' },
                    { label: `Todas (${horasP.entradas.length})`, value: 'todas' },
                  ]} />
                )}
              </Flex>
              {carregandoHoras ? <div style={{ padding: 20, textAlign: 'center' }}><Spin /></div>
                : falhaHoras ? <Alert type="error" showIcon message="Não foi possível carregar as horas da pessoa." description={falhaHoras}
                    action={<Button size="small" onClick={() => setTentativaHoras((t) => t + 1)}>Tentar de novo</Button>} />
                : horasP ? (<>
                  {horasMudaram(horasP.resumo, aberta) && !dados?.em_andamento && (
                    <Alert type="warning" showIcon style={{ marginBottom: 8 }} message="As horas mudaram depois do cálculo do mês."
                      description='A lista mostra as horas de agora. Use "Recalcular o mês" para o valor acompanhar.' />
                  )}
                  <Table<EntradaHora> rowKey="id" size="small" dataSource={entradasVisiveis(horasP.entradas, modoHoras)}
                    pagination={{ pageSize: 50, hideOnSinglePage: true }} scroll={{ x: 980 }}
                    locale={{ emptyText: modoHoras === 'sem' ? 'Nenhuma hora sem contrato.'
                      : horasP.usuarios_clockify === 0 ? 'Nenhuma hora no anotador, e a ficha desta pessoa não tem usuário do Clockify ligado neste mês.'
                      : 'Nenhuma hora anotada no mês.' }}
                    columns={[
                      { title: 'Dia', key: 'dia', width: 100, render: (_: any, e: EntradaHora) => diaCurto(e.dia, e.hora) },
                      { title: 'Onde', key: 'fonte', width: 95, render: (_: any, e: EntradaHora) => (e.fonte === 'clockify' ? 'Clockify' : 'Anotador') },
                      { title: 'Projeto ou centro', key: 'onde', width: 240, render: (_: any, e: EntradaHora) => e.onde || <Text type="secondary">sem projeto</Text> },
                      { title: 'Descrição', key: 'desc', render: (_: any, e: EntradaHora) => e.descricao || <Text type="secondary">—</Text> },
                      { title: 'Horas', key: 'h', align: 'right' as const, width: 90, render: (_: any, e: EntradaHora) => <TotalHoras minutos={minutosDe(e.horas)} alinhar="end" tamanhoDecimal={11} /> },
                      { title: 'Para onde foi', key: 'para', width: 300, render: (_: any, e: EntradaHora) => (
                        <span><Tag color={DESTINO_HORA[e.destino].cor}>{DESTINO_HORA[e.destino].rotulo}</Tag>{paraOndeFoi(e)}</span>) },
                    ]} />
                </>) : null}
            </div>
          )}
        </>)}
      </Modal>
    </div>
  );
}
